import React, { useContext, useEffect, useState } from "react";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import { CycleFilterContext } from "../overview/context/CycleFilterContext";
import { OverviewContext } from "../overview/context/OverviewContext";
import { FormattedMessage, injectIntl } from "react-intl";
import Row from "@amzn/meridian/row";
import Tooltip from "@amzn/meridian/tooltip";
import Box from "@amzn/meridian/box";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import Icon from "@amzn/meridian/icon";
import { PVAOverviewContext } from "../overview/context/PVAOverviewContext";
import { STOW_WIP, STOW_WIP_COMPLIANCE } from "../overview/utils/Constants";
import { PERCENTAGE } from "../outboundAmzl/util/Constants";
import { getStowWIPChartData } from "../overview/utils/utils";
import { getStowWipComplianceAPIEnabled } from "../handler/ParentDataHandler";
import { getStowWipComplianceData } from "../overview/network/OverviewNetworkAPI";

const WIPView = (props) => {
  const { settingsInsights, opsClockData, isLMOCSEnabledForOpsClockData } =
    props.breaksDataSources;
  const { cycles } = useContext(CycleFilterContext);
  const { viewModel, dispatch } = useContext(OverviewContext);
  const { pvaData } = useContext(PVAOverviewContext);
  const { targetData = {} } = pvaData;
  const selectedCycle = cycles.selectedCycles ? cycles.selectedCycles[0] : "";
  const [isComplianceApiEnabled, setIsComplianceApiEnabled] = useState(false);
  const [complianceData, setComplianceData] = useState(null);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);
  let wipCompliance = 0;

  useEffect(() => {
    if (props.wip.processName === STOW_WIP_COMPLIANCE) {
      getStowWipComplianceAPIEnabled().then((res) => {
        setIsComplianceApiEnabled(res.isStowWipComplianceEnabled === "true");
      });
    }
  }, []);

  const getWIPData = () => {
    dispatch({
      type: "LOAD_WIP_DATA",
      data: {
        filter: cycles.selectedCycles,
        dispatcher: dispatch,
        processName: props.wip.processName,
        selectedCycle,
      },
    });
  };

  const loadComplianceData = async () => {
    if (
      props.wip.processName === STOW_WIP_COMPLIANCE &&
      isComplianceApiEnabled &&
      !isLoadingCompliance
    ) {
      setIsLoadingCompliance(true);
      try {
        await getStowWipComplianceData(dispatch, selectedCycle);
        // Data will be available in viewModel after the context updates
        // We'll handle setting complianceData in a separate useEffect that watches viewModel
      } catch (error) {
        setComplianceData(null);
        setIsLoadingCompliance(false);
      }
    }
  };

  // Watch for changes in viewModel to update compliance data
  useEffect(() => {
    if (
      props.wip.processName === STOW_WIP_COMPLIANCE &&
      isLoadingCompliance &&
      viewModel.wip &&
      viewModel.wip.stowWipComplianceData
    ) {
      const complianceApiData = viewModel.wip.stowWipComplianceData;
      setComplianceData({
        nodeID: complianceApiData.nodeID,
        value: complianceApiData.value || 0,
      });
      setIsLoadingCompliance(false);
    }
  }, [viewModel.wip, isLoadingCompliance, props.wip.processName]);

  useEffect(() => {
    if (cycles.allCycles.length > 0) {
      getWIPData();
      loadComplianceData();
      const interval = setInterval(() => {
        if (!document.hidden) {
          getWIPData();
          loadComplianceData();
        }
      }, props.wip.refreshRate);
      return () => clearInterval(interval);
    }
  }, [cycles, isComplianceApiEnabled]);

  function getWipCompliance() {
    let dataList = pvaData.wip.dataPointList;
    const upperLimit = parseInt(pvaData.wipTargetData.STOW_WIP_UPPER, 10);
    const lowerLimit = parseInt(pvaData.wipTargetData.STOW_WIP_LOWER, 10);
    if (!dataList || dataList.length === 0) return "0" + PERCENTAGE;
    dataList = getStowWIPChartData(dataList, {
      isLMOCSEnabledForOpsClockData,
      settingsInsights,
      opsClockData,
      targetData,
      selectedCycle,
    });
    const numOfExcludedIntervals = dataList.filter(
      (item) => item.isExcluded,
    ).length;
    const wipIntervals = dataList.length - numOfExcludedIntervals;
    const compliantIntervals = dataList.filter(
      (dataPoint) =>
        dataPoint.metricValue >= lowerLimit &&
        dataPoint.metricValue <= upperLimit,
    ).length;
    if (wipIntervals === 0) return "100" + PERCENTAGE;
    return (
      ((compliantIntervals * 100) / wipIntervals).toFixed(2).toString() +
      PERCENTAGE
    );
  }

  wipCompliance =
    isComplianceApiEnabled && complianceData
      ? `${complianceData.value}${PERCENTAGE}`
      : getWipCompliance();

  return (
    <Box type="outline" spacingInset="400">
      <div>
        <div className="overview-fph">
          <Row width="50%" widths="fill" alignmentHorizontal="justify">
            <Heading level={2} type="h300" alignment="left">
              <FormattedMessage
                id={viewModel.wip[props.wip.processName].title}
                defaultMessage={
                  viewModel.wip[props.wip.processName].defaultMessage
                }
              />
              <Tooltip
                position="right"
                title={props.intl.formatMessage({
                  id: viewModel.wip[props.wip.processName].tooltip,
                  defaultMessage:
                    viewModel.wip[props.wip.processName].tooltipMessage,
                })}
              >
                <Icon
                  className={"custom-tool-tip"}
                  tokens={infoKnockoutTokens}
                />
              </Tooltip>
            </Heading>
          </Row>
        </div>

        <Row width="100%" widths={["grid-6", "grid-6"]}>
          <div>
            <Text type="b100">
              {props.wip.processName === STOW_WIP ? (
                <FormattedMessage
                  id="wip.target"
                  defaultMessage="Target (min/max)"
                />
              ) : props.wip.processName === STOW_WIP_COMPLIANCE ? (
                <FormattedMessage
                  id="wip.targetCompliance"
                  defaultMessage="Target"
                />
              ) : (
                ""
              )}
            </Text>
            {props.wip.processName === STOW_WIP ? (
              <Text type="d50">
                {" "}
                {pvaData.wipTargetData.STOW_WIP_LOWER
                  ? pvaData.wipTargetData.STOW_WIP_LOWER
                  : 0}
                {"/"}
                {pvaData.wipTargetData.STOW_WIP_UPPER
                  ? pvaData.wipTargetData.STOW_WIP_UPPER
                  : 0}{" "}
              </Text>
            ) : (
              ""
            )}
            {props.wip.processName === STOW_WIP_COMPLIANCE ? (
              <Text type="d50">
                {" "}
                {pvaData.wipTargetData.WIP_TARGET
                  ? pvaData.wipTargetData.WIP_TARGET + PERCENTAGE
                  : 0 + PERCENTAGE}{" "}
              </Text>
            ) : (
              ""
            )}
            {props.wip.processName === "expectedTimeToBuildWip" ? (
              <Text type="d50"> 8 </Text>
            ) : (
              ""
            )}
          </div>
          <div>
            <Text type="b100">
              {props.wip.processName === STOW_WIP ||
              props.wip.processName === STOW_WIP_COMPLIANCE ? (
                <FormattedMessage id="wip.actual" defaultMessage="Actual" />
              ) : (
                ""
              )}
            </Text>
            {props.wip.processName === STOW_WIP ? (
              viewModel.wip[STOW_WIP].actual <
                pvaData.wipTargetData.STOW_WIP_LOWER ||
              viewModel.wip[STOW_WIP].actual >
                pvaData.wipTargetData.STOW_WIP_UPPER ? (
                <Text type="d50" className="alert">
                  {" "}
                  {viewModel.wip[props.wip.processName].actual}{" "}
                </Text>
              ) : (
                <Text type="d50">
                  {" "}
                  {viewModel.wip[props.wip.processName].actual}{" "}
                </Text>
              )
            ) : (
              ""
            )}
            {props.wip.processName === STOW_WIP_COMPLIANCE ? (
              wipCompliance < pvaData.wipTargetData.WIP_TARGET ? (
                <Text type="d50" className="alert ">
                  {" "}
                  {wipCompliance}{" "}
                </Text>
              ) : (
                <Text type="d50"> {wipCompliance} </Text>
              )
            ) : (
              ""
            )}
          </div>
        </Row>
      </div>
    </Box>
  );
};

export default injectIntl(WIPView);



// WEBPACK FOOTER //
// ./src/components/WIP/WIPView.js